import { useState, useEffect, useRef } from 'react'
import { currentLocale } from '../../i18n'
import StatusPill from '../StatusPill'
import { PILL_FALLBACK } from '../../utils/purchaseStatus'

// Click-to-edit cells shared by the purchase request list (logistics and payment, edited in place).
// Click-to-edit single-line text cell — same interaction as the Warehouse Board.
export function TextCell({ value, editable, placeholder, onSave }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')

  if (!editable) return <span className="text-gray-800">{value || placeholder}</span>
  if (!editing) {
    return (
      <button type="button" onClick={() => { setDraft(value || ''); setEditing(true) }}
              className={`text-left w-full ${value ? 'text-gray-900' : 'text-gray-400'}`}>
        {value || placeholder}
      </button>
    )
  }
  const commit = () => { setEditing(false); if ((value || '') !== draft) onSave(draft || null) }
  return (
    <input autoFocus value={draft}
           onChange={e => setDraft(e.target.value)}
           onBlur={commit}
           onKeyDown={e => {
             if (e.key === 'Enter') commit()
             if (e.key === 'Escape') { setDraft(value || ''); setEditing(false) }
           }}
           className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" />
  )
}

export function NumberCell({ value, editable, placeholder, onSave }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')

  if (!editable) return <span className="text-gray-800">{value ?? placeholder}</span>
  if (!editing) {
    return (
      <button type="button" onClick={() => { setDraft(value ?? ''); setEditing(true) }}
              className={`text-left w-full ${value != null ? 'text-gray-900' : 'text-gray-400'}`}>
        {value ?? placeholder}
      </button>
    )
  }
  const commit = () => {
    setEditing(false)
    const num = draft === '' ? null : Number(draft)
    if ((value ?? null) !== num) onSave(num)
  }
  return (
    <input autoFocus type="number" step="0.5" value={draft}
           onChange={e => setDraft(e.target.value)}
           onBlur={commit}
           onKeyDown={e => {
             if (e.key === 'Enter') commit()
             if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false) }
           }}
           className="w-20 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400" />
  )
}

export function DateCell({ value, editable, onSave }) {
  const display = value ? new Date(value + 'T00:00:00').toLocaleDateString(currentLocale()) : '—'
  if (!editable) return <span className="text-gray-700 text-xs whitespace-nowrap">{display}</span>
  return (
    <input type="date" value={value || ''}
           onChange={e => onSave(e.target.value || null)}
           className="border border-transparent hover:border-gray-300 rounded px-1 py-0.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400" />
  )
}

export function PillCell({ value, options, styleMap, editable, label, clearLabel, onSelect, placeholder = '—' }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const pill = (
    <StatusPill
      value={value}
      styleMap={styleMap}
      text={value ? (label ? label(value) : value) : placeholder}
      placeholder={placeholder}
    />
  )
  if (!editable) return pill

  return (
    <span ref={ref} className="relative inline-flex">
      <button type="button" onClick={() => setOpen(o => !o)} className="focus:outline-none">{pill}</button>
      {open && (
        <div className="absolute z-30 top-7 left-0 min-w-[11rem] max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1">
          {value && clearLabel && (
            <button onClick={() => { onSelect(null); setOpen(false) }}
                    className="block w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50">
              {clearLabel}
            </button>
          )}
          {options.map(opt => (
            <button key={opt} onClick={() => { onSelect(opt); setOpen(false) }}
                    className="block w-full text-left px-3 py-1.5 hover:bg-gray-50">
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styleMap[opt] || PILL_FALLBACK}`}>
                {label ? label(opt) : opt}
              </span>
            </button>
          ))}
        </div>
      )}
    </span>
  )
}
